<template>
  <div>
    <!-- Header -->
    <div class="dashboard-header">
      <h2 class="dashboard-title">
        <i class="fas fa-dumbbell mr-3"></i>
        AWR Fitness Analytics Dashboard
      </h2>
      <p class="dashboard-subtitle">Real-time insights into AWR Fitness performance</p>
    </div>

    <!-- Summary Cards -->
    <div class="summary-cards">
      <div 
        v-for="(card, index) in summaryCards" 
        :key="card.key" 
        class="summary-card"
        :class="`card-${index + 1}`"
      >
        <div class="card-icon">
          <i class="fa-1x text-white" :class="card.icon"></i>
        </div>
        <div class="card-content">
          <div class="card-title">{{ card.title }}</div>
          <div class="card-value">{{ card.value }}</div>
          <div class="card-subtitle" v-if="card.subtitle">{{ card.subtitle }}</div>
        </div>
      </div>
    </div>

    <!-- Charts Section -->
    <div class="charts-section">
      <!-- Revenue by Category -->
      <div class="chart-card">
        <div class="chart-header">
          <div class="chart-title">
            <i class="fa-1x fas fa-chart-bar mr-2"></i>
            Revenue by Category
          </div>
          <div class="chart-subtitle">Monthly revenue breakdown</div>
        </div>
        <div class="chart-content">
          <apexchart type="bar" height="300" :options="revenueCatOptions" :series="revenueCatSeries" />
        </div>
      </div>

      <!-- Top Packages -->
      <div class="chart-card">
        <div class="chart-header">
          <div class="chart-title">
            <i class="fas fa-1x fa-trophy mr-2"></i>
            Top Packages
          </div>
          <div class="chart-subtitle">Best selling packages this month</div>
        </div>
        <div class="chart-content">
          <apexchart type="donut" height="300" :options="topPackageOptions" :series="topPackageSeries" />
        </div>
      </div>

      <!-- Top Personal Trainers -->
      <div class="chart-card">
        <div class="chart-header">
          <div class="chart-title">
            <i class="fas fa-1x fa-medal mr-2"></i>
            Top Personal Trainers
          </div>
          <div class="chart-subtitle">Most active trainers by check-ins</div>
        </div>
        <div class="chart-content">
          <apexchart type="bar" height="300" :options="topPTOptions" :series="topPTSeries" />
        </div>
      </div>
    </div>

    <!-- Package Expiry Reminder Section -->
    <div class="expiry-section">
      <div class="chart-card expiry-card">
        <div class="chart-header">
          <div class="chart-title">
            <i class="fas fa-exclamation-triangle fa-1x mr-2"></i>
            Package Expiry Reminder
          </div>
          <div class="chart-subtitle">
            {{ packageExpiryRows.length }} packages expiring within {{ statistics && statistics.reminder && statistics.reminder.package_expiry ? statistics.reminder.package_expiry.period_days : 30 }} days
          </div>
        </div>
        <div class="expiry-content">
          <div v-if="packageExpiryRows.length === 0" class="empty-state">
            <i class="fas fa-check-circle fa-1x"></i>
            <p>No packages expiring soon</p>
          </div>
          <div v-else class="modern-table-wrapper">
            <table class="modern-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Package</th>
                  <th>Expired Date</th>
                  <th>Fitness Consultant</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in packageExpiryRows" :key="item.sales_item_id" class="table-row">
                  <td class="member-cell">
                    <div class="member-info-table">
                      <div class="member-avatar-table" :class="`avatar-${(index % 4) + 1}`">
                        <i class="fas fa-user fa-1x text-white"></i>
                      </div>
                      <div class="member-details">
                        <div class="member-name-table">{{ item.member_name }}</div>
                      </div>
                    </div>
                  </td>
                  <td class="package-cell">
                    <div class="package-name">{{ item.package_name }}</div>
                  </td>
                  <td class="date-cell">
                    <div class="date-info">
                      <div class="expiry-date">{{ formatExpiryDate(item.expired_date) }}</div>
                      <div class="days-remaining" :class="getDaysClass(item.days_to_expired)">
                        {{ item.days_to_expired === 0 ? 'Hari ini' : item.days_to_expired + ' hari lagi' }}
                      </div>
                    </div>
                  </td>
                  <td class="fc-cell">
                    <div class="fc-name">{{ item.fitness_consultant_name }}</div>
                  </td>
                  <td class="status-cell">
                    <span class="status-badge" :class="getExpiryStatus(item.days_to_expired)">
                      {{ getExpiryStatusText(item.days_to_expired) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Member Birthdays Section -->
    <div class="birthday-section">
      <div class="chart-card birthday-card">
        <div class="chart-header">
          <div class="chart-title">
            <i class="fas fa-birthday-cake fa-1x mr-2"></i>
            Member Birthdays
          </div>
          <div class="chart-subtitle">Members celebrating this month</div>
        </div>
        <div class="birthday-content">
          <div v-if="sortedMembers.length === 0" class="empty-state">
            <i class="fas fa-calendar-day fa-1x"></i>
            <p>No birthdays this month</p>
          </div>
          <div v-else class="modern-table-wrapper">
            <table class="modern-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Birthday</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(member, index) in sortedMembers" :key="member.id" class="table-row">
                  <td class="member-cell">
                    <div class="member-info-table">
                      <div class="member-avatar-table" :class="`avatar-${(index % 4) + 1}`">
                        <i class="fas fa-user fa-1x text-white"></i>
                      </div>
                      <div class="member-details">
                        <div class="member-name-table">{{ member.name }}</div>
                        <div class="member-id">0{{ member.phone || 'No phone' }}</div>
                      </div>
                    </div>
                  </td>
                  <td class="date-cell">
                    <div class="date-info">
                      <div class="birthday-date">{{ formatDate(member.date_of_birth) }}</div>
                      <div class="birthday-age">{{ calculateAge(member.date_of_birth) }} years</div>
                    </div>
                  </td>
                  <td class="status-cell">
                    <span class="status-badge" :class="getBirthdayStatus(member.date_of_birth)">
                      {{ getBirthdayStatusText(member.date_of_birth) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import ApiService from "@/core/services/api.service";

export default {
  name: "DashboardStatistics",
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    return {
      // Data tabel ulang tahun
      members: [],
      fields: [
        { key: "name", label: "Nama" },
        { key: "date_of_birth", label: "Tanggal Lahir" },
      ],

      // Raw statistics dari API
      statistics: null,

      // Chart states
      revenueCatOptions: {
        chart: { 
          toolbar: { show: false },
          fontFamily: 'inherit',
          foreColor: '#666'
        },
        colors: ['#FF6B35', '#5B6EDF', '#F5E942', '#7DD3C0', '#FF8C8C'],
        plotOptions: {
          bar: {
            borderRadius: 8,
            columnWidth: '60%',
            distributed: true
          }
        },
        xaxis: { 
          categories: [],
          labels: { style: { colors: '#666', fontSize: '12px' } }
        },
        yaxis: {
          labels: {
            style: { colors: '#666', fontSize: '12px' },
            formatter: (v) => "Rp " + new Intl.NumberFormat("id-ID").format(v),
          },
        },
        dataLabels: {
          enabled: false,
        },
        tooltip: {
          theme: 'light',
          y: {
            formatter: (v) => "Rp " + new Intl.NumberFormat("id-ID").format(v),
          },
        },
        grid: {
          borderColor: '#f1f1f1',
          strokeDashArray: 4,
        },
        legend: { show: false }
      },
      revenueCatSeries: [{ name: "Revenue", data: [] }],

      topPackageOptions: {
        chart: { 
          toolbar: { show: false },
          fontFamily: 'inherit'
        },
        colors: ['#FF6B35', '#5B6EDF', '#F5E942', '#7DD3C0', '#FF8C8C', '#FFB347'],
        dataLabels: {
          enabled: true,
          style: { fontSize: '12px', fontWeight: 'bold', colors: ['#fff'] }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '50%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: '#FF6B35',
                  formatter: function (w) {
                    return w.globals.seriesTotals.reduce((a, b) => {
                      return a + b
                    }, 0)
                  }
                },
                value: {
                  show: true,
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#2c3e50',
                  offsetY: 8
                }
              }
            }
          }
        },
        tooltip: {
          theme: 'light',
          y: {
            formatter: (v) => new Intl.NumberFormat("id-ID").format(v) + " packages",
          },
        },
        legend: {
          position: 'bottom',
          fontSize: '12px',
          fontWeight: 'normal',
          markers: { width: 8, height: 8, radius: 4 }
        }
      },
      topPackageSeries: [],

      topPTOptions: {
        chart: { 
          toolbar: { show: false },
          fontFamily: 'inherit',
          foreColor: '#666'
        },
        colors: ['#5B6EDF'],
        plotOptions: {
          bar: {
            borderRadius: 8,
            columnWidth: '70%',
            horizontal: true,
          }
        },
        xaxis: { 
          categories: [],
          labels: { style: { colors: '#666', fontSize: '12px' } }
        },
        yaxis: {
          labels: {
            style: { colors: '#666', fontSize: '12px' }
          },
        },
        dataLabels: {
          enabled: true,
          style: { fontSize: '11px', fontWeight: 'bold', colors: ['#fff'] }
        },
        tooltip: {
          theme: 'light',
          y: {
            formatter: (v) => new Intl.NumberFormat("id-ID").format(v) + " check-ins",
          },
        },
        grid: {
          borderColor: '#f1f1f1',
          strokeDashArray: 4,
        }
      },
      topPTSeries: [{ name: "Check-ins", data: [] }],
    };
  },
  computed: {
    summaryCards() {
      const s = this.statistics;
      if (!s) {
        // skeleton kosong saat loading
        return [
          { key: "checkin_today", title: "Check-in Hari Ini", value: "-" },
          { key: "checkin_month", title: "Check-in Bulan Ini", value: "-" },
          { key: "avg_daily", title: "Rata-rata Check-in (so far)", value: "-" },
          { key: "revenue_total", title: "Revenue Bulan Ini", value: "-" },
          { key: "transactions", title: "Transaksi Bulan Ini", value: "-" },
          { key: "new_users", title: "Member Baru Bulan Ini", value: "-" },
        ];
      }

      const checkinToday = s.checkin?.today?.total ?? 0;
      const checkinMonth = s.checkin?.month?.total ?? 0;
      const avgDaily = s.checkin?.month?.avg_daily_so_far ?? 0;
      const rev = s.sales?.month?.revenue_total ?? 0;
      const trx = s.sales?.month?.transactions_count ?? 0;
      const newUsers = s.people?.new_users_month ?? 0;

      return [
        {
          key: "checkin_today",
          title: "Today's Check-ins",
          value: this.formatNumber(checkinToday),
          subtitle: `Check-in: ${this.formatNumber(s.checkin?.today?.without_pt ?? 0)} | PT Session: ${this.formatNumber(s.checkin?.today?.with_pt ?? 0)}`,
          icon: "fas fa-calendar-day",
        },
        {
          key: "checkin_month",
          title: "Monthly Check-ins",
          value: this.formatNumber(checkinMonth),
          subtitle: `Check-In: ${this.formatNumber(s.checkin?.month?.without_pt ?? 0)} | PT Session: ${this.formatNumber(s.checkin?.month?.with_pt ?? 0)}`,
          icon: "fas fa-chart-line",
        },
        {
          key: "avg_daily",
          title: "Daily Average Check-ins",
          value: this.formatNumber(avgDaily),
          subtitle: `${s.checkin?.month?.days_in_month ?? "-"} days this month`,
          icon: "fas fa-calculator",
        },
        {
          key: "revenue_total",
          title: "Monthly Revenue",
          value: "Rp " + this.formatNumber(rev),
          subtitle: this.periodText,
          icon: "fas fa-money-bill-wave",
        },
        {
          key: "transactions",
          title: "Transactions",
          value: this.formatNumber(trx),
          subtitle: this.periodText,
          icon: "fas fa-receipt",
        },
        {
          key: "new_users",
          title: "New Members",
          value: this.formatNumber(newUsers),
          subtitle: this.periodText,
          icon: "fas fa-user-plus",
        },
      ];
    },
    packageExpiryRows() {
      return this.statistics?.reminder?.package_expiry?.rows || [];
    },

    periodText() {
      const m = this.statistics?.period?.month;
      if (!m) return "";
      
      try {
        // Handle formats like "2025-09" or "09"
        let year, month;
        
        if (m.includes('-')) {
          [year, month] = m.split('-');
        } else {
          year = new Date().getFullYear();
          month = m;
        }
        
        // Create date object and format it
        const date = new Date(parseInt(year), parseInt(month) - 1, 1);
        return date.toLocaleDateString('en-US', { 
          month: 'long', 
          year: 'numeric' 
        });
      } catch {
        return m ? `Month ${m}` : "";
      }
    },
    
    sortedMembers() {
      if (!this.members || this.members.length === 0) return [];
      
      return [...this.members].sort((a, b) => {
        if (!a.date_of_birth || !b.date_of_birth) return 0;
        
        try {
          const today = new Date();
          const currentYear = today.getFullYear();
          
          // Convert dates to this year's birthday
          const dateA = new Date(a.date_of_birth);
          const dateB = new Date(b.date_of_birth);
          
          const birthdayA = new Date(currentYear, dateA.getMonth(), dateA.getDate());
          const birthdayB = new Date(currentYear, dateB.getMonth(), dateB.getDate());
          
          // Calculate days from today
          const daysFromTodayA = Math.ceil((birthdayA.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          const daysFromTodayB = Math.ceil((birthdayB.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          
          // Sort by proximity to today (today first, then upcoming, then recent)
          if (daysFromTodayA === 0 && daysFromTodayB !== 0) return -1;
          if (daysFromTodayB === 0 && daysFromTodayA !== 0) return 1;
          
          // Both are either upcoming or both are past
          if (daysFromTodayA >= 0 && daysFromTodayB >= 0) {
            return daysFromTodayA - daysFromTodayB; // Closer upcoming dates first
          }
          
          if (daysFromTodayA < 0 && daysFromTodayB < 0) {
            return daysFromTodayB - daysFromTodayA; // More recent past dates first
          }
          
          // One is upcoming, one is past - upcoming first
          if (daysFromTodayA >= 0) return -1;
          if (daysFromTodayB >= 0) return 1;
          
          return 0;
        } catch {
          return 0;
        }
      });
    },
  },
  async mounted() {
      // 1) Birthday list
      const { data: birthdayData } = await ApiService.get("user/birthday-list?role_slug=member");
      this.members = Array.isArray(birthdayData) ? birthdayData : [];

      // 2) Statistics
      const { data: statData } = await ApiService.get("sales/statistic");
      this.statistics = statData;

      // Build charts dari statistics
      this.buildRevenueByCategory();
      this.buildTopPackages();
      this.buildTopPT();
  },
  methods: {
    // ===== Builders =====
    buildRevenueByCategory() {
      const revenueByCategory = this.statistics?.sales?.month?.revenue_by_category || {};
      const categories = Object.keys(revenueByCategory);
      const values = categories.map((k) => Number(revenueByCategory[k] || 0));

      this.revenueCatOptions = {
        ...this.revenueCatOptions,
        xaxis: { categories },
      };
      this.revenueCatSeries = [{ name: "Revenue", data: values }];
    },

    buildTopPackages() {
      const top = this.statistics?.sales?.month?.top_packages || [];
      const categories = top.map((x) => x.package_name || "(Unknown Package)");
      const qty = top.map((x) => Number(x.total_qty || 0));

      this.topPackageOptions = {
        ...this.topPackageOptions,
        labels: categories,
      };
      this.topPackageSeries = qty;
    },

    buildTopPT() {
      const top = this.statistics?.people?.top_pt_by_checkin || [];
      const categories = top.map((x) => x.personal_trainer_name || "(Unknown PT)");
      const val = top.map((x) => Number(x.total_checkin || 0));

      this.topPTOptions = {
        ...this.topPTOptions,
        xaxis: { categories },
      };
      this.topPTSeries = [{ name: "Check-in", data: val }];
    },

    // ===== Utils =====
    formatNumber(v) {
      return new Intl.NumberFormat("id-ID").format(v || 0);
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      try {
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return "-";
        return d.toLocaleDateString("id-ID", { day: "2-digit", month: "short" });
      } catch {
        return "-";
      }
    },
    
    calculateAge(dateStr) {
      if (!dateStr) return 0;
      try {
        const today = new Date();
        const birthDate = new Date(dateStr);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        return age;
      } catch {
        return 0;
      }
    },
    
    getBirthdayStatus(dateStr) {
      if (!dateStr) return 'upcoming';
      try {
        const today = new Date();
        const birthDate = new Date(dateStr);
        const currentYear = today.getFullYear();
        const thisYearBirthday = new Date(currentYear, birthDate.getMonth(), birthDate.getDate());
        
        const diffTime = thisYearBirthday.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) return 'today';
        if (diffDays > 0 && diffDays <= 7) return 'upcoming';
        if (diffDays < 0 && diffDays >= -7) return 'recent';
        return 'upcoming';
      } catch {
        return 'upcoming';
      }
    },
    
    getBirthdayStatusText(dateStr) {
      const status = this.getBirthdayStatus(dateStr);
      switch (status) {
        case 'today': return 'Today!';
        case 'upcoming': return 'Soon';
        case 'recent': return 'Recent';
        default: return 'Upcoming';
      }
    },

    formatExpiryDate(dateStr) {
      if (!dateStr) return "-";
      try {
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return "-";
        return d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
      } catch {
        return "-";
      }
    },

    getDaysClass(days) {
      if (days === 0) return 'days-expired';
      if (days <= 7) return 'days-urgent';
      if (days <= 14) return 'days-soon';
      return 'days-normal';
    },

    getExpiryStatus(days) {
      if (days === 0) return 'expired';
      if (days <= 7) return 'urgent';
      if (days <= 14) return 'soon';
      return 'normal';
    },

    getExpiryStatusText(days) {
      if (days === 0) return 'Expired!';
      if (days <= 7) return 'Urgent';
      if (days <= 14) return 'Soon';
      return 'Active';
    },
  },
};
</script>

<style scoped>
.dashboard-header {
  text-align: center;
  margin-bottom: 40px;
  padding: 20px 0;
}

.dashboard-title {
  color: #2c3e50;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.dashboard-title i {
  color: #FF6B35;
  font-size: 2.2rem;
}

.dashboard-subtitle {
  color: #6c757d;
  font-size: 1.1rem;
  font-weight: 400;
  margin: 0;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 40px;
}

.summary-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  gap: 20px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.summary-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #FF6B35, #FF8C42);
}

.summary-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(255, 107, 53, 0.15);
}

.summary-card.card-1::before { background: linear-gradient(90deg, #FF6B35, #FF8C42); }
.summary-card.card-2::before { background: linear-gradient(90deg, #5B6EDF, #7B7FE5); }
.summary-card.card-3::before { background: linear-gradient(90deg, #F5E942, #F7EB5A); }
.summary-card.card-4::before { background: linear-gradient(90deg, #7DD3C0, #8FD8C6); }
.summary-card.card-5::before { background: linear-gradient(90deg, #FF8C8C, #FFA0A0); }
.summary-card.card-6::before { background: linear-gradient(90deg, #FFB347, #FFC56B); }

.card-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  flex-shrink: 0;
}

.card-1 .card-icon { background: linear-gradient(135deg, #FF6B35, #FF8C42); }
.card-2 .card-icon { background: linear-gradient(135deg, #5B6EDF, #7B7FE5); }
.card-3 .card-icon { background: linear-gradient(135deg, #F5E942, #F7EB5A); }
.card-4 .card-icon { background: linear-gradient(135deg, #7DD3C0, #8FD8C6); }
.card-5 .card-icon { background: linear-gradient(135deg, #FF8C8C, #FFA0A0); }
.card-6 .card-icon { background: linear-gradient(135deg, #FFB347, #FFC56B); }

.card-content {
  flex: 1;
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.card-value {
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 4px;
  line-height: 1.2;
}

.card-subtitle {
  margin-top: 12px;
  font-size: 0.9rem;
  color: #888;
  font-weight: 500;
}

.charts-section {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 40px;
}

.expiry-section {
  margin-bottom: 24px;
}

.expiry-content {
  padding: 0;
}

.package-cell {
  width: 25%;
}

.package-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: #2c3e50;
  line-height: 1.4;
}

.fc-cell {
  width: 20%;
}

.fc-name {
  font-size: 0.85rem;
  color: #6c757d;
  font-weight: 500;
}

.expiry-date {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.days-remaining {
  font-size: 0.8rem;
  font-weight: 600;
}

.days-expired { color: #e74c3c; }
.days-urgent  { color: #e67e22; }
.days-soon    { color: #f39c12; }
.days-normal  { color: #27ae60; }

.status-badge.expired {
  background: linear-gradient(135deg, #e74c3c, #c0392b);
  color: white;
}

.status-badge.urgent {
  background: linear-gradient(135deg, #e67e22, #d35400);
  color: white;
}

.status-badge.soon {
  background: linear-gradient(135deg, #f39c12, #e67e22);
  color: white;
}

.status-badge.normal {
  background: linear-gradient(135deg, #27ae60, #2ecc71);
  color: white;
}

.birthday-section {
  margin-top: 24px;
}

.chart-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
  transition: all 0.3s ease;
}

.chart-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.chart-header {
  padding: 24px 24px 16px;
  border-bottom: 1px solid #f1f3f4;
  background: linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%);
}

.chart-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
}

.chart-title i {
  color: #FF6B35;
  margin-right: 8px;
}

.chart-subtitle {
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 500;
}

.chart-content {
  padding: 20px;
}

.birthday-card {
  grid-column: span 1;
}

.birthday-content {
  padding: 0;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
}

.empty-state i {
  font-size: 3rem;
  color: #dee2e6;
  margin-bottom: 16px;
}

.empty-state p {
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
}

.modern-table-wrapper {
  overflow: hidden;
  border-radius: 0 0 16px 16px;
}

.expiry-section .modern-table-wrapper {
  max-height: 420px;
  overflow-y: auto;
  overflow-x: hidden;
}

.expiry-section .modern-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.birthday-section .modern-table-wrapper {
  max-height: 420px;
  overflow-y: auto;
  overflow-x: hidden;
}

.birthday-section .modern-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  font-family: inherit;
}

.modern-table thead th {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 16px 20px;
  text-align: left;
  font-weight: 700;
  font-size: 0.85rem;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #dee2e6;
}

.modern-table tbody .table-row {
  transition: all 0.2s ease;
  border-bottom: 1px solid #f1f3f4;
}

.modern-table tbody .table-row:hover {
  background: #f8f9fa;
  transform: scale(1.01);
}

.modern-table tbody .table-row:last-child {
  border-bottom: none;
}

.modern-table td {
  padding: 20px;
  vertical-align: middle;
}

.member-cell {
  width: 50%;
}

.member-info-table {
  display: flex;
  align-items: center;
  gap: 16px;
}

.member-avatar-table {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  flex-shrink: 0;
  font-weight: bold;
}

.avatar-1 { background: linear-gradient(135deg, #FF6B35, #FF8C42); }
.avatar-2 { background: linear-gradient(135deg, #5B6EDF, #7B7FE5); }
.avatar-3 { background: linear-gradient(135deg, #F5E942, #F7EB5A); }
.avatar-4 { background: linear-gradient(135deg, #7DD3C0, #8FD8C6); }

.member-details {
  flex: 1;
}

.member-name-table {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.member-id {
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 500;
}

.date-cell {
  width: 30%;
}

.date-info {
  text-align: center;
}

.birthday-date {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.birthday-age {
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 500;
}

.status-cell {
  width: 20%;
  text-align: center;
}

.status-badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.today {
  background: linear-gradient(135deg, #FF6B35, #FF8C42);
  color: white;
}

.status-badge.upcoming {
  background: linear-gradient(135deg, #5B6EDF, #7B7FE5);
  color: white;
}

.status-badge.recent {
  background: linear-gradient(135deg, #7DD3C0, #8FD8C6);
  color: white;
}

@media (max-width: 1024px) {
  .summary-cards {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(3, 1fr);
  }
  
  .charts-section {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

@media (max-width: 768px) {

  .summary-cards {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(6, 1fr);
    gap: 16px;
  }
  
  .charts-section {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .birthday-section {
    margin-top: 16px;
  }
  
  .dashboard-title {
    font-size: 2rem;
  }
  
  .summary-card {
    padding: 20px;
  }
  
  .card-value {
    font-size: 1.5rem;
  }
  
  .modern-table thead th {
    padding: 12px 16px;
    font-size: 0.75rem;
  }
  
  .modern-table td {
    padding: 16px;
  }
  
  .member-avatar-table {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }
  
  .member-name-table {
    font-size: 0.9rem;
  }
  
  .member-id {
    font-size: 0.75rem;
  }
  
  .birthday-date {
    font-size: 0.85rem;
  }
  
  .birthday-age {
    font-size: 0.75rem;
  }
  
  .status-badge {
    padding: 4px 8px;
    font-size: 0.7rem;
  }
}

@media (max-width: 480px) {
  .summary-card {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }
  
  .card-icon {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
  
  .member-info-table {
    gap: 12px;
  }
  
  .member-avatar-table {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }
  
  .modern-table thead th {
    padding: 10px 12px;
  }
  
  .modern-table td {
    padding: 12px;
  }
  
  .member-cell {
    width: 45%;
  }
  
  .date-cell {
    width: 35%;
  }
  
  .status-cell {
    width: 20%;
  }
}

/* Animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.summary-card,
.chart-card {
  animation: fadeInUp 0.6s ease-out;
}

.summary-card:nth-child(1) { animation-delay: 0.1s; }
.summary-card:nth-child(2) { animation-delay: 0.2s; }
.summary-card:nth-child(3) { animation-delay: 0.3s; }
.summary-card:nth-child(4) { animation-delay: 0.4s; }
.summary-card:nth-child(5) { animation-delay: 0.5s; }
.summary-card:nth-child(6) { animation-delay: 0.6s; }
</style>
